import { useEffect, useMemo, useState } from "react";
import Store from "../store/Store";

/* Haversine */
function distance(a, b) {
  const R = 6371e3;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const φ1 = toRad(a.lat);
  const φ2 = toRad(b.lat);
  const Δφ = toRad(b.lat - a.lat);
  const Δλ = toRad(b.lng - a.lng);
  return (
    2 *
    R *
    Math.asin(
      Math.sqrt(
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
          Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
      )
    )
  );
}

/* centroid */
function centroid(polygon) {
  if (!polygon?.length) return null;
  let lat = 0,
    lng = 0;
  polygon.forEach((p) => {
    lat += parseFloat(p.latitude);
    lng += parseFloat(p.longitude);
  });
  return { lat: lat / polygon.length, lng: lng / polygon.length };
}

/* point-in-polygon (ray casting) */
function pointInPolygon(point, polygon) {
  let x = point.lat,
    y = point.lng;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    let xi = parseFloat(polygon[i].latitude),
      yi = parseFloat(polygon[i].longitude);
    let xj = parseFloat(polygon[j].latitude),
      yj = parseFloat(polygon[j].longitude);

    let intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export default function useLocation() {
  const { parcels: storeParcels } = Store.getState();
  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const [currentParcel, setCurrentParcel] = useState(null);
  const [nearestParcel, setNearestParcel] = useState(null);
  const [loading, setLoading] = useState(false);

  const parcels = useMemo(() => {
    return (storeParcels || []).map((p) => ({ ...p, coordinates: p.coordinates || [] }));
  }, [storeParcels]);

  useEffect(() => {
    if (!navigator?.geolocation) {
      setError("A böngésző nem támogatja a geolokációt");
      return;
    }
    const watcher = navigator.geolocation.watchPosition(
      (pos) => {
        setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => setError(err?.message || "Helymeghatározási hiba"),
      { enableHighAccuracy: true }
    );
    return () => navigator.geolocation.clearWatch(watcher);
  }, []);

  useEffect(() => {
    if (!position || !parcels?.length) return;

    let found = null;
    for (const p of parcels) {
      for (const polygon of p.coordinates) {
        if (pointInPolygon(position, polygon)) {
          found = p;
          break;
        }
      }
      if (found) break;
    }
    setCurrentParcel(found);

    if (!found) {
      let minDist = Infinity;
      let closest = null;
      parcels.forEach((p) => {
        p.coordinates.forEach((polygon) => {
          const c = centroid(polygon);
          if (c) {
            const d = distance(position, c);
            if (d < minDist) {
              minDist = d;
              closest = p;
            }
          }
        });
      });

      if (closest) {
        // normalizáljuk a parcel_id / parcel_name mezőket, hogy a caller könnyen megtalálja
        const pid = closest.parcel_id ?? closest.id ?? closest._id ?? null;
        const pname =
          closest.parcel_name ??
          closest.name ??
          closest.title ??
          (closest.parcels && closest.parcels[0]?.parcel_name) ??
          null;

        setNearestParcel({ ...closest, parcel_id: pid, parcel_name: pname, dist: minDist });
      } else {
        setNearestParcel(null);
      }
    } else {
      setNearestParcel(null);
    }
  }, [position, parcels]);

    const requestLocation = () => {
        return new Promise((resolve, reject) => {
            if (!navigator?.geolocation) {
            const errMsg = "A böngésző nem támogatja a geolokációt";
            setError(errMsg);
            reject(new Error(errMsg));
            return;
            }

            setLoading(true);

            navigator.geolocation.getCurrentPosition(
            (pos) => {
                const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setPosition(coords);
                setLoading(false);
                resolve(coords);
            },
            (err) => {
                const errMsg = err?.message || "Helymeghatározási hiba";
                setError(errMsg);
                setLoading(false);
                reject(new Error(errMsg));
            },
            { enableHighAccuracy: true }
            );
        });
    };



  return { position, error, currentParcel, nearestParcel, loading, requestLocation };
}
